import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { RepertoryService } from '../../../service/admin/repertory.service';
import { ToasterService } from '../../../service/common/toaster.service';
import {
  MateriaMedicaSection,
  RepertoryPagination,
  RepertoryRemedy,
  RepertoryRubricResult,
  RepertorySource,
  RepertoryStatus,
} from '../../../models/repertory.model';
import {
  ENGLISH_MM_QUICK_TERMS,
  GERMAN_REPERTORY_QUICK_TERMS,
  isGermanRepertory,
  RepertoryQuickTerm,
  translateGermanDisplayText,
  translateGermanPath,
} from './repertory-translations';

@Component({
  selector: 'app-repertory-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DropdownModule,
    InputTextModule,
    TagModule,
    TooltipModule,
  ],
  templateUrl: './repertory-panel.component.html',
  styleUrl: './repertory-panel.component.scss',
  host: {
    '[class.rep-panel--page]': 'mode === "page"',
    '[class.rep-panel--dialog]': 'mode === "dialog"',
  },
})
export class RepertoryPanelComponent implements OnInit, OnChanges, OnDestroy {
  @Input() mode: 'page' | 'dialog' = 'page';
  @Input() selectionMode = false;
  @Input() initialSymptom = '';
  @Input() targetLabel = '';
  @Input() preferredPotency = '';

  @Output() remedySelected = new EventEmitter<RepertoryRemedy>();

  potencyPickerRemedyId: number | null = null;
  potencyPickerMmSectionId: number | null = null;

  sidebarWidth = 320;
  sidebarDragging = false;
  private readonly sidebarMinWidth = 260;
  private readonly sidebarMaxWidth = 460;
  private readonly resultsMinWidth = 320;
  private readonly splitterWidth = 8;
  private readonly sidebarStorageKey = 'rx_repertory_sidebar_width';
  private sidebarResizeStartX = 0;
  private sidebarResizeStartWidth = 0;

  @ViewChild('repWorkspace') repWorkspaceRef?: ElementRef<HTMLElement>;

  status: RepertoryStatus | null = null;
  loadingStatus = true;
  searching = false;
  hasSearched = false;
  showAdvancedFilters = false;

  repertories: RepertorySource[] = [];
  materiaMedicas: RepertorySource[] = [];

  selectedRepertory = 'kent-de';
  selectedMateriaMedica = 'boericke';
  symptom = '';
  remedyFilter = '';
  minWeight = 0;

  rubricResults: RepertoryRubricResult[] = [];
  mmResults: MateriaMedicaSection[] = [];
  pagination: RepertoryPagination | null = null;
  activeTab: 'repertory' | 'mm' = 'repertory';

  expandedRubricIds = new Set<number>();
  recentlyAdded: string[] = [];

  weightOptions = [
    { label: 'All (0–4)', value: 0 },
    { label: '1+ only', value: 1 },
    { label: '2+ only', value: 2 },
    { label: '3+ only', value: 3 },
    { label: '4 only', value: 4 },
  ];

  get selectedRepertoryMeta(): RepertorySource | undefined {
    return this.repertories.find((r) => r.abbrev === this.selectedRepertory);
  }

  get activeSourceLabel(): string {
    if (this.activeTab === 'mm') {
      const source = this.materiaMedicas.find((item) => item.abbrev === this.selectedMateriaMedica);
      return source?.title || 'Materia medica';
    }

    return this.selectedRepertoryMeta?.title || 'Repertory';
  }

  get symptomPlaceholder(): string {
    if (this.activeTab === 'mm') {
      return 'e.g. burning thirst, restless anxiety…';
    }

    if (this.isGermanRepertorySelected) {
      return 'e.g. fever, headache, cough…';
    }

    return 'e.g. fever, headache…';
  }

  get isGermanRepertorySelected(): boolean {
    return isGermanRepertory(this.selectedRepertoryMeta?.language, this.selectedRepertory);
  }

  get languageBadge(): string | null {
    if (this.activeTab === 'mm') {
      return 'EN';
    }

    if (this.isGermanRepertorySelected) {
      return 'DE→EN';
    }

    const language = this.selectedRepertoryMeta?.language?.toLowerCase();

    if (language === 'en') {
      return 'EN';
    }

    return null;
  }

  get activeFilterCount(): number {
    let count = 0;

    if (this.remedyFilter.trim()) {
      count += 1;
    }

    if (this.activeTab === 'repertory' && this.minWeight > 0) {
      count += 1;
    }

    return count;
  }

  get minWeightLabel(): string {
    return this.weightOptions.find((option) => option.value === this.minWeight)?.label ?? 'All grades';
  }

  isQuickTermActive(term: RepertoryQuickTerm): boolean {
    return this.symptom.trim().toLowerCase() === term.query.toLowerCase()
      || this.symptom.trim().toLowerCase() === term.label.toLowerCase();
  }

  setMinWeight(value: number): void {
    this.minWeight = value;
  }

  get quickSearchTerms(): RepertoryQuickTerm[] {
    if (this.activeTab === 'mm') {
      return ENGLISH_MM_QUICK_TERMS;
    }

    if (this.isGermanRepertorySelected) {
      return GERMAN_REPERTORY_QUICK_TERMS;
    }

    return ENGLISH_MM_QUICK_TERMS;
  }

  get searchLanguageHint(): string | null {
    if (this.activeTab === 'mm') {
      return 'Materia medica prose search — section titles rank first, then ranked body excerpts (Boericke).';
    }

    if (this.isGermanRepertorySelected) {
      return 'Kent rubrics are stored in German. Search in English — common terms are translated automatically. English glosses appear under each rubric.';
    }

    if (this.selectedRepertoryMeta?.language?.toLowerCase() === 'en') {
      return 'English rubrics — use English keywords.';
    }

    return null;
  }

  get hasResults(): boolean {
    return this.activeTab === 'repertory'
      ? this.rubricResults.length > 0
      : this.mmResults.length > 0;
  }

  get showNoResults(): boolean {
    return this.hasSearched && !this.searching && !this.hasResults && !!this.symptom.trim();
  }

  get showInitialState(): boolean {
    return !this.hasSearched && !this.searching && !this.symptom.trim();
  }

  get resultRangeLabel(): string | null {
    if (!this.pagination) {
      return null;
    }

    const start = (this.pagination.current_page - 1) * this.pagination.per_page + 1;
    const end = Math.min(this.pagination.current_page * this.pagination.per_page, this.pagination.total);

    return `${start}–${end} of ${this.pagination.total.toLocaleString()}`;
  }

  get addedCount(): number {
    return this.recentlyAdded.length;
  }

  constructor(
    private repertoryService: RepertoryService,
    private toast: ToasterService,
  ) {}

  ngOnInit(): void {
    this.symptom = this.initialSymptom;
    this.showAdvancedFilters = this.mode === 'page';
    this.restoreSidebarWidth();
    this.loadStatus();
    this.loadSources();
  }

  ngOnDestroy(): void {
    this.stopSidebarResize();
  }

  private restoreSidebarWidth(): void {
    if (this.mode !== 'dialog') {
      return;
    }

    const stored = Number(localStorage.getItem(this.sidebarStorageKey));
    if (!Number.isFinite(stored)) {
      return;
    }

    this.sidebarWidth = this.clampSidebarWidth(stored);
  }

  private clampSidebarWidth(width: number, layoutWidth?: number): number {
    const containerWidth = layoutWidth ?? this.repWorkspaceRef?.nativeElement?.clientWidth ?? 0;
    const maxByLayout = containerWidth > 0
      ? containerWidth - this.resultsMinWidth - this.splitterWidth
      : this.sidebarMaxWidth;
    const maxWidth = Math.max(
      this.sidebarMinWidth,
      Math.min(this.sidebarMaxWidth, maxByLayout || this.sidebarMaxWidth),
    );
    return Math.max(this.sidebarMinWidth, Math.min(maxWidth, Math.round(width)));
  }

  startSidebarResize(event: MouseEvent): void {
    if (this.mode !== 'dialog') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.sidebarDragging = true;
    this.sidebarResizeStartX = event.clientX;
    this.sidebarResizeStartWidth = this.sidebarWidth;
    document.body.classList.add('rep-workspace-resizing');
  }

  @HostListener('document:mousemove', ['$event'])
  onSidebarResizeMove(event: MouseEvent): void {
    if (!this.sidebarDragging) {
      return;
    }

    event.preventDefault();
    const delta = event.clientX - this.sidebarResizeStartX;
    this.sidebarWidth = this.clampSidebarWidth(this.sidebarResizeStartWidth + delta);
  }

  @HostListener('document:mouseup')
  onSidebarResizeEnd(): void {
    if (!this.sidebarDragging) {
      return;
    }

    this.stopSidebarResize();
    localStorage.setItem(this.sidebarStorageKey, String(this.sidebarWidth));
  }

  @HostListener('window:resize')
  onWorkspaceResize(): void {
    if (this.mode !== 'dialog') {
      return;
    }

    this.sidebarWidth = this.clampSidebarWidth(this.sidebarWidth);
  }

  private stopSidebarResize(): void {
    this.sidebarDragging = false;
    document.body.classList.remove('rep-workspace-resizing');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialSymptom'] && !changes['initialSymptom'].firstChange) {
      this.symptom = this.initialSymptom;
    }
  }

  loadStatus(): void {
    this.loadingStatus = true;
    this.repertoryService.getStatus().subscribe({
      next: (res) => {
        this.status = res.data;
        this.loadingStatus = false;
        this.maybeAutoSearch();
      },
      error: () => {
        this.loadingStatus = false;
        this.toast.error('Repertory', 'Unable to reach repertory API.');
      },
    });
  }

  loadSources(): void {
    this.repertoryService.listRepertories().subscribe({
      next: (res) => {
        this.repertories = res.data ?? [];
        if (this.repertories.length && !this.repertories.some((r) => r.abbrev === this.selectedRepertory)) {
          this.selectedRepertory = this.repertories[0].abbrev;
        }
      },
    });

    this.repertoryService.listMateriaMedicas().subscribe({
      next: (res) => {
        this.materiaMedicas = res.data ?? [];
        if (this.materiaMedicas.length && !this.materiaMedicas.some((m) => m.abbrev === this.selectedMateriaMedica)) {
          this.selectedMateriaMedica = this.materiaMedicas[0].abbrev;
        }
      },
      error: () => {
        this.toast.error('Repertory', 'Unable to load materia medica sources.');
      },
    });
  }

  setActiveTab(tab: 'repertory' | 'mm'): void {
    this.activeTab = tab;
    this.pagination = null;
    this.rubricResults = [];
    this.mmResults = [];
    this.hasSearched = false;
    this.expandedRubricIds.clear();
  }

  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  quickSearch(term: RepertoryQuickTerm): void {
    this.symptom = term.query;
    this.search();
  }

  clearSearch(): void {
    this.symptom = '';
    this.remedyFilter = '';
    this.rubricResults = [];
    this.mmResults = [];
    this.pagination = null;
    this.hasSearched = false;
    this.expandedRubricIds.clear();
  }

  search(page = 1): void {
    const symptom = this.symptom.trim();
    if (!symptom) {
      this.toast.warn('Repertory', 'Enter a symptom to search.');
      return;
    }

    if (!this.status?.ready) {
      this.toast.warn('Repertory', this.status?.message ?? 'Repertory data is not loaded yet.');
      return;
    }

    this.searching = true;
    this.hasSearched = true;
    this.expandedRubricIds.clear();

    if (this.activeTab === 'mm') {
      const materiaMedica = this.selectedMateriaMedica || this.materiaMedicas[0]?.abbrev || 'boericke';

      this.repertoryService.searchMateriaMedica({
        materiaMedica,
        symptom,
        page,
        remedy: this.remedyFilter,
      }).subscribe({
        next: (res) => {
          this.mmResults = res.data ?? [];
          this.rubricResults = [];
          this.pagination = res.pagination;
          this.searching = false;
        },
        error: (err) => {
          this.searching = false;
          const message = err?.error?.message ?? 'Materia medica search failed.';
          this.toast.error('Repertory', message);
        },
      });
      return;
    }

    this.repertoryService.searchRepertory({
      repertory: this.selectedRepertory,
      symptom,
      page,
      minWeight: this.minWeight,
      remedy: this.remedyFilter,
    }).subscribe({
      next: (res) => {
        this.rubricResults = res.data ?? [];
        this.mmResults = [];
        this.pagination = res.pagination;
        this.searching = false;
        this.expandFirstRubric();
      },
      error: () => {
        this.searching = false;
        this.toast.error('Repertory', 'Repertory search failed.');
      },
    });
  }

  goToPage(page: number): void {
    if (!this.pagination || page < 1 || page > this.pagination.last_page) {
      return;
    }
    this.search(page);
  }

  pathSegments(fullPath: string): string[] {
    return fullPath
      .split(',')
      .map((segment) => segment.trim())
      .filter(Boolean);
  }

  rubricEnglishText(rubric: RepertoryRubricResult): string | null {
    return rubric.text_en ?? translateGermanDisplayText(rubric.text);
  }

  rubricEnglishPath(rubric: RepertoryRubricResult): string | null {
    return rubric.full_path_en ?? translateGermanPath(rubric.full_path);
  }

  pathSegmentsEnglish(fullPath: string, rubric?: RepertoryRubricResult): string[] {
    if (rubric?.full_path_en) {
      return this.pathSegments(rubric.full_path_en);
    }

    const translated = translateGermanPath(fullPath);
    return this.pathSegments(translated ?? fullPath);
  }

  mmMatchLabel(section: MateriaMedicaSection): string {
    switch (section.match_in) {
      case 'heading':
        return 'Section title';
      case 'remedy':
        return 'Remedy chapter';
      default:
        return 'Body text';
    }
  }

  mmDisplayExcerpt(section: MateriaMedicaSection): string {
    return (section.excerpt || section.content || section.heading || '').trim();
  }

  isRubricExpanded(id: number): boolean {
    return this.expandedRubricIds.has(id);
  }

  toggleRubric(id: number): void {
    if (this.expandedRubricIds.has(id)) {
      this.expandedRubricIds.delete(id);
      return;
    }

    this.expandedRubricIds.add(id);
  }

  weightLabel(weight: number): string {
    return weight > 0 ? String(weight) : '0';
  }

  weightClass(weight: number | undefined): string {
    switch (weight) {
      case 4:
        return 'rep-weight--4';
      case 3:
        return 'rep-weight--3';
      case 2:
        return 'rep-weight--2';
      default:
        return 'rep-weight--1';
    }
  }

  remedyDisplayName(remedy: RepertoryRemedy): string {
    return (remedy.name || remedy.abbrev || '').trim();
  }

  isClinicAvailable(remedy: RepertoryRemedy): boolean {
    return remedy.clinic_available === true;
  }

  clinicAvailabilityTooltip(remedy: RepertoryRemedy): string {
    if (!this.isClinicAvailable(remedy) || !remedy.clinic_matches?.length) {
      return 'Not found in your clinic medicine database.';
    }

    return remedy.clinic_matches
      .map((match) => `${match.name.trim()} · ${match.potency} (${match.code})`)
      .join('\n');
  }

  selectRemedy(remedy: RepertoryRemedy, event?: Event): void {
    this.handleRemedySelection(remedy, 'repertory', remedy.id, event);
  }

  selectRemedyPotency(
    remedy: RepertoryRemedy,
    match: NonNullable<RepertoryRemedy['clinic_matches']>[number],
    event?: Event,
  ): void {
    event?.stopPropagation();
    this.potencyPickerRemedyId = null;
    this.emitRemedySelection({ ...remedy, selected_clinic_match: match });
  }

  selectRemedyPotencyFromMm(
    section: MateriaMedicaSection,
    match: NonNullable<MateriaMedicaSection['clinic_matches']>[number],
    event?: Event,
  ): void {
    event?.stopPropagation();
    this.potencyPickerMmSectionId = null;
    this.emitRemedySelection(this.mmSectionAsRemedy(section, match));
  }

  isPotencyPickerOpen(remedyId: number): boolean {
    return this.potencyPickerRemedyId === remedyId;
  }

  isMmPotencyPickerOpen(sectionId: number): boolean {
    return this.potencyPickerMmSectionId === sectionId;
  }

  isMmClinicAvailable(section: MateriaMedicaSection): boolean {
    return section.clinic_available === true;
  }

  mmClinicAvailabilityTooltip(section: MateriaMedicaSection): string {
    if (!this.isMmClinicAvailable(section) || !section.clinic_matches?.length) {
      return 'Not found in your clinic medicine database.';
    }

    return section.clinic_matches
      .map((match) => `${match.name.trim()} · ${match.potency} (${match.code})`)
      .join('\n');
  }

  mmSectionAsRemedy(
    section: MateriaMedicaSection,
    selectedMatch?: MateriaMedicaSection['selected_clinic_match'],
  ): RepertoryRemedy {
    return {
      id: section.id,
      abbrev: section.remedy_abbrev || section.chapter_heading || '',
      name: section.chapter_heading || '',
      clinic_available: section.clinic_available,
      clinic_matches: section.clinic_matches,
      selected_clinic_match: selectedMatch,
    };
  }

  private handleRemedySelection(
    remedy: RepertoryRemedy,
    source: 'repertory' | 'mm',
    pickerId: number,
    event?: Event,
  ): void {
    event?.stopPropagation();

    if (!this.selectionMode) {
      return;
    }

    const matches = remedy.clinic_matches ?? [];

    if (!matches.length) {
      this.clearPotencyPickers();
      this.emitRemedySelection(remedy);
      return;
    }

    if (matches.length === 1) {
      this.clearPotencyPickers();
      this.emitRemedySelection({ ...remedy, selected_clinic_match: matches[0] });
      return;
    }

    const preferred = this.normalizePotency(this.preferredPotency);
    if (preferred) {
      const byPotency = matches.filter(
        (match) => this.normalizePotency(match.potency) === preferred,
      );
      if (byPotency.length === 1) {
        this.clearPotencyPickers();
        this.emitRemedySelection({ ...remedy, selected_clinic_match: byPotency[0] });
        return;
      }
    }

    if (source === 'repertory') {
      this.potencyPickerRemedyId = this.potencyPickerRemedyId === pickerId ? null : pickerId;
      this.potencyPickerMmSectionId = null;
      return;
    }

    this.potencyPickerMmSectionId = this.potencyPickerMmSectionId === pickerId ? null : pickerId;
    this.potencyPickerRemedyId = null;
  }

  private clearPotencyPickers(): void {
    this.potencyPickerRemedyId = null;
    this.potencyPickerMmSectionId = null;
  }

  private emitRemedySelection(remedy: RepertoryRemedy): void {
    const name = this.remedyDisplayName(remedy);
    if (!name) {
      return;
    }

    this.potencyPickerRemedyId = null;
    this.trackRecentAdd(name);
    this.remedySelected.emit(remedy);
  }

  private normalizePotency(potency?: string | null): string {
    return String(potency ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  selectRemedyFromMm(section: MateriaMedicaSection, event?: Event): void {
    if (!section.chapter_heading) {
      return;
    }

    this.handleRemedySelection(this.mmSectionAsRemedy(section), 'mm', section.id, event);
  }

  private trackRecentAdd(name: string): void {
    const normalized = name.trim();
    if (!normalized) {
      return;
    }

    this.recentlyAdded = [
      normalized,
      ...this.recentlyAdded.filter((item) => item.toLowerCase() !== normalized.toLowerCase()),
    ].slice(0, 8);
  }

  private expandFirstRubric(): void {
    const first = this.rubricResults[0];
    if (first) {
      this.expandedRubricIds.add(first.id);
    }
  }

  private maybeAutoSearch(): void {
    if (this.mode !== 'dialog' || !this.initialSymptom.trim() || !this.status?.ready || this.hasSearched) {
      return;
    }

    this.symptom = this.initialSymptom.trim();
    this.search();
  }
}
